import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { authAPI } from '../services/api';
import { getFromStorage, saveToStorage, removeFromStorage } from '../utils/storage';

interface User {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar?: string;
  role: 'rider' | 'driver';
}

interface AuthState {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<{ success: boolean; message?: string }>;
  register: (userData: RegisterData) => Promise<{ success: boolean; message?: string }>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
}

interface RegisterData {
  name: string;
  email: string;
  phone: string;
  password: string;
  role: 'rider' | 'driver';
  country_code?: string;
}

type AuthAction =
  | { type: 'RESTORE_TOKEN'; token: string | null; user: User | null }
  | { type: 'SIGN_IN'; token: string; user: User }
  | { type: 'SIGN_OUT' }
  | { type: 'SET_LOADING'; isLoading: boolean }
  | { type: 'UPDATE_USER'; userData: Partial<User> };

const initialState: AuthState = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
};

const authReducer = (prevState: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'RESTORE_TOKEN':
      return {
        ...prevState,
        token: action.token,
        user: action.user,
        isLoading: false,
        isAuthenticated: !!action.token,
      };
    case 'SIGN_IN':
      return {
        ...prevState,
        token: action.token,
        user: action.user,
        isAuthenticated: true,
        isLoading: false,
      };
    case 'SIGN_OUT':
      return {
        ...prevState,
        token: null,
        user: null,
        isAuthenticated: false,
        isLoading: false,
      };
    case 'SET_LOADING':
      return {
        ...prevState,
        isLoading: action.isLoading,
      };
    case 'UPDATE_USER':
      return {
        ...prevState,
        user: prevState.user ? { ...prevState.user, ...action.userData } : null,
      };
    default:
      return prevState;
  }
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);


  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = await getFromStorage('authToken');
        const storedUser = await getFromStorage('user');

        if (storedToken && storedUser) {
          dispatch({
            type: 'RESTORE_TOKEN',
            token: storedToken,
            user: JSON.parse(storedUser),
          });
        } else {
          dispatch({ type: 'RESTORE_TOKEN', token: null, user: null });
        }
      } catch (e) {
        console.error('Failed to restore authentication state:', e);
        dispatch({ type: 'RESTORE_TOKEN', token: null, user: null });
      }
    };

    initializeAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; message?: string }> => {
    try {
      dispatch({ type: 'SET_LOADING', isLoading: true });
      const response = await authAPI.login(email, password);


      console.log('Login API response:', response);






      let user, access_token, refresh_token;

      if (response && typeof response === 'object') {
        if ((response as any).data && (response as any).data.user) {

          user = (response as any).data.user;
          access_token = (response as any).data.access_token;
          refresh_token = (response as any).data.refresh_token;
        } else if (response.user && response.access_token) {

          user = response.user;
          access_token = response.access_token;
          refresh_token = response.refresh_token;
        } else {
          console.error('Unexpected login response structure:', JSON.stringify(response, null, 2));
          return { success: false, message: 'Invalid response from server. Please check console for details.' };
        }
      } else {
        console.error('Invalid login response type:', typeof response, response);
        return { success: false, message: 'Invalid response from server' };
      }

      if (user && access_token) {
        await saveToStorage('authToken', access_token);
        if (refresh_token) {
          await saveToStorage('refreshToken', refresh_token);
        }
        await saveToStorage('user', JSON.stringify(user));

        dispatch({ type: 'SIGN_IN', token: access_token, user });
        return { success: true };
      } else {
        console.error('Missing required fields in login response:', { 
          hasUser: !!user, 
          hasAccessToken: !!access_token,
          responseKeys: Object.keys(response || {})
        });
        return { success: false, message: 'Invalid response from server: missing user or token' };
      }
    } catch (error: any) {
      console.error('Login error:', error);
      console.error('Error response:', error?.response?.data);
      console.error('Error status:', error?.response?.status);


      let errorMessage = 'Network error. Please try again.';
      if (error?.response?.data) {
        errorMessage = error.response.data.message || 
                      error.response.data.error || 
                      error.response.data.error?.message ||
                      JSON.stringify(error.response.data);
      } else if (error?.message) {
        errorMessage = error.message;
      }

      return { success: false, message: errorMessage };
    } finally {
      dispatch({ type: 'SET_LOADING', isLoading: false });
    }
  };

  const register = async (userData: RegisterData): Promise<{ success: boolean; message?: string }> => {
    try {
      dispatch({ type: 'SET_LOADING', isLoading: true });
      const response = await authAPI.register(userData);

      if (response && response.user && response.access_token) {
        const { access_token, refresh_token, user } = response;

        await saveToStorage('authToken', access_token);
        await saveToStorage('refreshToken', refresh_token);
        await saveToStorage('user', JSON.stringify(user));

        dispatch({ type: 'SIGN_IN', token: access_token, user });
        return { success: true };
      } else {
        return { success: false, message: 'Invalid response from server' };
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Network error. Please try again.';
      return { success: false, message: errorMessage };
    } finally {
      dispatch({ type: 'SET_LOADING', isLoading: false });
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    } finally {
      await removeFromStorage('authToken');
      await removeFromStorage('user');
      dispatch({ type: 'SIGN_OUT' });
    }
  };

  const updateUser = (userData: Partial<User>) => {
    dispatch({ type: 'UPDATE_USER', userData });
    saveToStorage('user', JSON.stringify({ ...state.user, ...userData }));
  };

  const value: AuthContextType = {
    ...state,
    login,
    register,
    logout,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
